/// <reference types="@webgpu/types" />

/**
 * The WebGPU renderer for the network views: one pass, four draw calls a frame.
 *
 * Nodes, then group regions, then guides, then references - the last of which is a second draw
 * rather than part of the third because guides and references need opposite blending, and blending
 * is fixed per pipeline. Four rather than two, and still independent of how large the estate is.
 *
 * The order is the compositing order, since only the node pass writes depth. Nodes first so
 * everything after them can be occluded by them; regions next, so a line drawn through a group's
 * cloud stays a line rather than being washed into it.
 *
 * Split along the same lines as `createSurfaceGpuRenderer`, because the allocations have different
 * lifetimes and rebuilding any of them per frame is the usual way a GPU view ends up slower than a
 * canvas one. The pipelines and the sampler-free bind group are per view; the instance buffers
 * change when the layout or the focus does; the uniform buffer is the only thing written every
 * frame.
 *
 * Multisampled at 4x. Nodes are discs cut out of a quad by a `discard` and edges are one- to
 * two-pixel ribbons, so aliasing is not a polish concern here - an unmultisampled frame has
 * visibly jagged node rims and edges that flicker in and out as the graph turns, which reads as the
 * data changing.
 */

import type { Size, ViewCamera } from '../../../types';
import { DEFAULT_LIGHTING, type LightingModel } from '../../../render/lighting';
import {
  NODE_FLOATS,
  LINE_FLOATS,
  VOLUME_FLOATS,
  ARROW_FLOATS,
  PLANAR_MARKER_FLOATS,
  type NetworkInstances,
} from '../packNetwork';
import type { AmbientOcclusion, EdgeBlend } from '../networkTheme';
import { NETWORK_SHADER } from './networkShaders';
import {
  drawNetworkMarkerAtlas,
  MARKER_ATLAS_HEIGHT,
  MARKER_ATLAS_WIDTH,
} from '../networkMarkerAtlas';
import {
  packNetworkUniforms,
  NETWORK_UNIFORM_FLOAT_COUNT,
  type NetworkFlow,
} from './networkUniforms';

const SAMPLE_COUNT = 4;

const DEPTH_FORMAT = 'depth24plus';
const OCCLUSION_DEPTH_FORMAT = 'rgba32float';

const NODE_STRIDE = NODE_FLOATS * 4;

/** Read by both node pipelines, which run `nodeVertex` over the same buffer. */
const NODE_VERTEX_BUFFER: GPUVertexBufferLayout = {
  arrayStride: NODE_STRIDE,
  /* Per instance, not per vertex: the six vertices of the billboard come from `vertex_index` and
     carry no data of their own, so there is no per-vertex stream at all and a node costs 32 bytes
     rather than six copies of itself. */
  stepMode: 'instance',
  attributes: [
    { shaderLocation: 0, offset: 0, format: 'float32x3' },
    { shaderLocation: 1, offset: 12, format: 'float32' },
    { shaderLocation: 2, offset: 16, format: 'float32x3' },
    { shaderLocation: 3, offset: 28, format: 'float32' },
  ],
};

const LINE_STRIDE = LINE_FLOATS * 4;

const VOLUME_STRIDE = VOLUME_FLOATS * 4;

const ARROW_STRIDE = ARROW_FLOATS * 4;

const PLANAR_MARKER_STRIDE = PLANAR_MARKER_FLOATS * 4;

/** Ten facets up the cone and ten across its base, three vertices each. */
const ARROW_VERTICES = 60;

type NetworkGpuDrawArgs = {
  camera: ViewCamera;
  /** In device pixels. Must match the canvas backing store. */
  viewport: Size;
  pixelRatio: number;
  lighting?: LightingModel;
  ambientOcclusion: AmbientOcclusion;
  surface: readonly [number, number, number];
  /** How reference edges combine. Guides are never additive - see the draw. */
  edgeBlend: EdgeBlend;
  /** How a reference says which way it points. */
  flow: NetworkFlow;
};

export type NetworkGpuRenderer = {
  /** Uploads new instance data. Cheap to call only when the instances actually change. */
  setInstances: (instances: NetworkInstances) => void;
  draw: (args: NetworkGpuDrawArgs) => void;
  /** Releases every GPU allocation. Must be called, or the device leaks buffers per view. */
  destroy: VoidFunction;
};

export type CreateNetworkGpuRendererArgs = {
  device: GPUDevice;
  canvas: HTMLCanvasElement;
};

export const createNetworkGpuRenderer = ({
  device,
  canvas,
}: CreateNetworkGpuRendererArgs): NetworkGpuRenderer => {
  const context = canvas.getContext('webgpu');
  if (!context) {
    throw new Error('WebGPU canvas context unavailable');
  }

  /**
   * Drawn through an `-srgb` view of the canvas, which is what puts the lighting in linear space.
   *
   * The shaders work in linear radiance throughout, and the encode has to happen somewhere. Doing
   * it at the end of each fragment would leave *blending* on encoded values - so two half-covering
   * edges would not sum to the light they carry, and the additive pass, whose whole reading is that
   * sum, would be wrong in exactly the place it matters. An `-srgb` target blends in linear and
   * encodes on store, which is the one arrangement where both are right.
   *
   * `viewFormats` rather than configuring the canvas as `-srgb` directly: the canvas format has to
   * stay the preferred one for the compositor, and this asks for the second view of the same
   * texture. Falls back to the plain format where the pair is unavailable, which costs the linear
   * blending and leaves the picture a little contrastier rather than broken.
   */
  const format = navigator.gpu.getPreferredCanvasFormat();
  const srgbFormat = `${format}-srgb` as GPUTextureFormat;
  const renderFormat = ((): GPUTextureFormat => {
    try {
      context.configure({
        device,
        format,
        alphaMode: 'premultiplied',
        viewFormats: [srgbFormat],
      });
      return srgbFormat;
    } catch {
      context.configure({ device, format, alphaMode: 'premultiplied' });
      return format;
    }
  })();

  const module = device.createShaderModule({ code: NETWORK_SHADER });

  const uniformBuffer = device.createBuffer({
    size: NETWORK_UNIFORM_FLOAT_COUNT * 4,
    usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST,
  });

  const nodePipeline = device.createRenderPipeline({
    layout: 'auto',
    vertex: {
      module,
      entryPoint: 'nodeVertex',
      buffers: [NODE_VERTEX_BUFFER],
    },
    fragment: {
      module,
      entryPoint: 'nodeFragment',
      /* No blending. Dimming is mixed toward the surface color in the shader instead, which keeps
         this pass opaque - so it needs no depth sort, and the depth it writes is honest for the
         line pass that follows. */
      targets: [{ format: renderFormat }],
    },
    primitive: { topology: 'triangle-list', cullMode: 'none' },
    depthStencil: {
      format: DEPTH_FORMAT,
      depthWriteEnabled: true,
      depthCompare: 'less',
    },
    multisample: { count: SAMPLE_COUNT },
  });

  /** The nodes again, writing their depth, id and emphasis for the occlusion pass to read. */
  const nodeDepthPipeline = device.createRenderPipeline({
    layout: 'auto',
    vertex: {
      module,
      entryPoint: 'nodeVertex',
      buffers: [NODE_VERTEX_BUFFER],
    },
    fragment: {
      module,
      entryPoint: 'nodeDepthFragment',
      targets: [{ format: OCCLUSION_DEPTH_FORMAT }],
    },
    primitive: { topology: 'triangle-list', cullMode: 'none' },
    depthStencil: {
      format: DEPTH_FORMAT,
      depthWriteEnabled: true,
      depthCompare: 'less',
    },
  });

  /**
   * One full-screen triangle that multiplies what the nodes drew by the occlusion: the color
   * blend is `source * destination`, and alpha is left as it was.
   */
  const occlusionPipeline = device.createRenderPipeline({
    layout: 'auto',
    vertex: { module, entryPoint: 'occlusionVertex' },
    fragment: {
      module,
      entryPoint: 'occlusionFragment',
      targets: [
        {
          format: renderFormat,
          blend: {
            color: { srcFactor: 'dst', dstFactor: 'zero', operation: 'add' },
            alpha: { srcFactor: 'zero', dstFactor: 'one', operation: 'add' },
          },
        },
      ],
    },
    primitive: { topology: 'triangle-list' },
    depthStencil: {
      format: DEPTH_FORMAT,
      depthWriteEnabled: false,
      depthCompare: 'always',
    },
    multisample: { count: SAMPLE_COUNT },
  });

  /**
   * The line pipeline, once per blend mode.
   *
   * Both read the same buffer and the same shader, and differ only in the destination factor. The
   * fragment shader needs no branch of its own: it already emits premultiplied color, which is
   * `source + destination * (1 - alpha)` under one set of factors and `source + destination` - the
   * sum of every edge that covered the pixel - under the other.
   *
   * Built up front rather than on a theme change, because a pipeline is compiled and the theme
   * changes while a reader is looking at it.
   */
  const createLinePipeline = (blend: EdgeBlend): GPURenderPipeline =>
    device.createRenderPipeline({
      layout: 'auto',
      vertex: {
        module,
        entryPoint: 'lineVertex',
        buffers: [
          {
            arrayStride: LINE_STRIDE,
            stepMode: 'instance',
            attributes: [
              { shaderLocation: 0, offset: 0, format: 'float32x3' },
              { shaderLocation: 1, offset: 12, format: 'float32x3' },
              { shaderLocation: 2, offset: 24, format: 'float32x4' },
              { shaderLocation: 3, offset: 40, format: 'float32' },
              { shaderLocation: 4, offset: 44, format: 'float32x3' },
              { shaderLocation: 5, offset: 56, format: 'float32x2' },
            ],
          },
        ],
      },
      fragment: {
        module,
        entryPoint: 'lineFragment',
        targets: [
          {
            format: renderFormat,
            /* Premultiplied source, which is what the fragment shader emits and what the canvas is
               configured for. */
            blend: {
              color: {
                srcFactor: 'one',
                dstFactor: blend === 'additive' ? 'one' : 'one-minus-src-alpha',
                operation: 'add',
              },
              /* Alpha follows color, so an additive pass accumulates coverage as well as light and
                 saturates at the same rate. The canvas clears opaque, so what this reaches is the
                 8-bit clamp rather than anything a compositor reads. */
              alpha: {
                srcFactor: 'one',
                dstFactor: blend === 'additive' ? 'one' : 'one-minus-src-alpha',
                operation: 'add',
              },
            },
          },
        ],
      },
      primitive: { topology: 'triangle-list', cullMode: 'none' },
      depthStencil: {
        format: DEPTH_FORMAT,
        /* Tested but not written. Edges are translucent, and a translucent fragment that writes
           depth rejects everything behind it - so whichever edge happened to be drawn first would
           hide the rest of the bundle it belongs to, and a bundle would read as one line. Nodes
           still occlude the edges behind them, because the node pass wrote real depth first. */
        depthWriteEnabled: false,
        depthCompare: 'less',
      },
      multisample: { count: SAMPLE_COUNT },
    });

  const linePipeline = createLinePipeline('alpha');
  const additiveLinePipeline = createLinePipeline('additive');

  /**
   * The region pipeline, once per blend mode for the same reason the line one is.
   *
   * Depth is tested and not written, and the fragment supplies its own: a region's test depth is
   * its *near* surface, so a mark in front of the group hides the cloud and every mark inside it
   * or behind it does not. Writing that depth as well would have the nearest region reject every
   * other one behind it, which for two groups seen through each other is one of them missing.
   */
  const createVolumePipeline = (blend: EdgeBlend): GPURenderPipeline =>
    device.createRenderPipeline({
      layout: 'auto',
      vertex: {
        module,
        entryPoint: 'volumeVertex',
        buffers: [
          {
            arrayStride: VOLUME_STRIDE,
            stepMode: 'instance',
            attributes: [
              { shaderLocation: 0, offset: 0, format: 'float32x3' },
              { shaderLocation: 1, offset: 12, format: 'float32' },
              { shaderLocation: 2, offset: 16, format: 'float32x3' },
              { shaderLocation: 3, offset: 28, format: 'float32' },
            ],
          },
        ],
      },
      fragment: {
        module,
        entryPoint: 'volumeFragment',
        targets: [
          {
            format: renderFormat,
            blend: {
              color: {
                srcFactor: 'one',
                dstFactor: blend === 'additive' ? 'one' : 'one-minus-src-alpha',
                operation: 'add',
              },
              alpha: {
                srcFactor: 'one',
                dstFactor: blend === 'additive' ? 'one' : 'one-minus-src-alpha',
                operation: 'add',
              },
            },
          },
        ],
      },
      primitive: { topology: 'triangle-list', cullMode: 'none' },
      depthStencil: {
        format: DEPTH_FORMAT,
        depthWriteEnabled: false,
        depthCompare: 'less',
      },
      multisample: { count: SAMPLE_COUNT },
    });

  const volumePipeline = createVolumePipeline('alpha');
  const additiveVolumePipeline = createVolumePipeline('additive');

  /**
   * Arrowheads, as geometry rather than as billboards.
   *
   * The sixty vertices of a ten-sided cone come from `vertex_index` like every other pass here, so
   * there is still no per-vertex stream - the cone is computed from the instance's heading and the
   * vertex's own number. Opaque and depth-writing for the same reason the node pass is.
   */
  const arrowPipeline = device.createRenderPipeline({
    layout: 'auto',
    vertex: {
      module,
      entryPoint: 'arrowVertex',
      buffers: [
        {
          arrayStride: ARROW_STRIDE,
          stepMode: 'instance',
          attributes: [
            { shaderLocation: 0, offset: 0, format: 'float32x3' },
            { shaderLocation: 1, offset: 12, format: 'float32x3' },
            { shaderLocation: 2, offset: 24, format: 'float32x2' },
            { shaderLocation: 3, offset: 32, format: 'float32x3' },
            { shaderLocation: 4, offset: 44, format: 'float32' },
            { shaderLocation: 5, offset: 48, format: 'float32x2' },
          ],
        },
      ],
    },
    fragment: {
      module,
      entryPoint: 'arrowFragment',
      targets: [{ format: renderFormat }],
    },
    primitive: { topology: 'triangle-list', cullMode: 'none' },
    depthStencil: {
      format: DEPTH_FORMAT,
      depthWriteEnabled: true,
      depthCompare: 'less',
    },
    multisample: { count: SAMPLE_COUNT },
  });

  /**
   * The flat glyphs, as a strip the fragment shader samples.
   *
   * Uploaded once and kept for the renderer's life: the artwork is notation, not data, so it does
   * not change with the graph, the theme or the camera. Black and white, with the shader picking
   * the theme's ink and surface off the luminance - see `networkMarkerAtlas`.
   */
  const atlas = drawNetworkMarkerAtlas();
  const markerTexture = device.createTexture({
    size: [MARKER_ATLAS_WIDTH, MARKER_ATLAS_HEIGHT],
    format: 'rgba8unorm',
    usage:
      GPUTextureUsage.TEXTURE_BINDING |
      GPUTextureUsage.COPY_DST |
      GPUTextureUsage.RENDER_ATTACHMENT,
  });
  if (atlas) {
    device.queue.copyExternalImageToTexture(
      { source: atlas },
      { texture: markerTexture },
      [MARKER_ATLAS_WIDTH, MARKER_ATLAS_HEIGHT]
    );
  }
  const markerSampler = device.createSampler({
    magFilter: 'linear',
    minFilter: 'linear',
  });

  const planarMarkerPipeline = device.createRenderPipeline({
    layout: 'auto',
    vertex: {
      module,
      entryPoint: 'planarMarkerVertex',
      buffers: [
        {
          arrayStride: PLANAR_MARKER_STRIDE,
          stepMode: 'instance',
          attributes: [
            { shaderLocation: 0, offset: 0, format: 'float32x3' },
            { shaderLocation: 1, offset: 12, format: 'float32x3' },
            { shaderLocation: 2, offset: 24, format: 'float32x2' },
            { shaderLocation: 3, offset: 32, format: 'float32' },
            { shaderLocation: 4, offset: 36, format: 'float32x3' },
          ],
        },
      ],
    },
    fragment: {
      module,
      entryPoint: 'planarMarkerFragment',
      /* Premultiplied, like the line pass: a glyph is antialiased by the rasteriser that drew the
         atlas, so its edges arrive as partial coverage rather than as a hard cut. */
      targets: [
        {
          format: renderFormat,
          blend: {
            color: {
              srcFactor: 'one',
              dstFactor: 'one-minus-src-alpha',
              operation: 'add',
            },
            alpha: {
              srcFactor: 'one',
              dstFactor: 'one-minus-src-alpha',
              operation: 'add',
            },
          },
        },
      ],
    },
    primitive: { topology: 'triangle-list', cullMode: 'none' },
    /* Tests depth but does not write it. The glyph is mostly transparent, and an opaque depth
       write would punch its own bounding box through whatever is behind it. */
    depthStencil: {
      format: DEPTH_FORMAT,
      depthWriteEnabled: false,
      depthCompare: 'less',
    },
    multisample: { count: SAMPLE_COUNT },
  });

  const planarMarkerBindGroup = device.createBindGroup({
    layout: planarMarkerPipeline.getBindGroupLayout(0),
    entries: [
      { binding: 0, resource: { buffer: uniformBuffer } },
      { binding: 1, resource: markerSampler },
      { binding: 2, resource: markerTexture.createView() },
    ],
  });

  /* Two bind groups over one buffer. `layout: 'auto'` derives a distinct layout per pipeline, so
     a single bind group cannot be bound to both even though the struct is identical. */
  const nodeBindGroup = device.createBindGroup({
    layout: nodePipeline.getBindGroupLayout(0),
    entries: [{ binding: 0, resource: { buffer: uniformBuffer } }],
  });
  const nodeDepthBindGroup = device.createBindGroup({
    layout: nodeDepthPipeline.getBindGroupLayout(0),
    entries: [{ binding: 0, resource: { buffer: uniformBuffer } }],
  });
  const lineBindGroup = device.createBindGroup({
    layout: linePipeline.getBindGroupLayout(0),
    entries: [{ binding: 0, resource: { buffer: uniformBuffer } }],
  });
  const additiveLineBindGroup = device.createBindGroup({
    layout: additiveLinePipeline.getBindGroupLayout(0),
    entries: [{ binding: 0, resource: { buffer: uniformBuffer } }],
  });
  const volumeBindGroup = device.createBindGroup({
    layout: volumePipeline.getBindGroupLayout(0),
    entries: [{ binding: 0, resource: { buffer: uniformBuffer } }],
  });
  const additiveVolumeBindGroup = device.createBindGroup({
    layout: additiveVolumePipeline.getBindGroupLayout(0),
    entries: [{ binding: 0, resource: { buffer: uniformBuffer } }],
  });
  const arrowBindGroup = device.createBindGroup({
    layout: arrowPipeline.getBindGroupLayout(0),
    entries: [{ binding: 0, resource: { buffer: uniformBuffer } }],
  });

  let nodeBuffer: GPUBuffer | null = null;
  let lineBuffer: GPUBuffer | null = null;
  let volumeBuffer: GPUBuffer | null = null;
  let arrowBuffer: GPUBuffer | null = null;
  let planarMarkerBuffer: GPUBuffer | null = null;
  let nodeCount = 0;
  let lineCount = 0;
  let guideCount = 0;
  let volumeCount = 0;
  let arrowCount = 0;
  let planarMarkerCount = 0;

  let depthTexture: GPUTexture | null = null;
  let multisampleTexture: GPUTexture | null = null;
  let occlusionAttachments: OcclusionAttachments | null = null;
  let attachmentSize: Size = { width: 0, height: 0 };

  /**
   * Reallocates only when the instance count outgrows the buffer.
   *
   * A focus change repacks every instance to redistribute emphasis, so this is reached on every
   * hover with the same counts as the frame before. Reallocating there would destroy and recreate
   * two buffers per pointer move.
   */
  const uploadInstances = (
    existing: GPUBuffer | null,
    data: Float32Array
  ): GPUBuffer | null => {
    if (data.byteLength === 0) {
      existing?.destroy();
      return null;
    }

    if (existing && existing.size >= data.byteLength) {
      device.queue.writeBuffer(existing, 0, data);
      return existing;
    }

    existing?.destroy();
    const created = device.createBuffer({
      size: data.byteLength,
      usage: GPUBufferUsage.VERTEX | GPUBufferUsage.COPY_DST,
    });
    device.queue.writeBuffer(created, 0, data);
    return created;
  };

  const ensureAttachments = (
    viewport: Size
  ): { depth: GPUTexture; color: GPUTexture } => {
    if (
      depthTexture &&
      multisampleTexture &&
      attachmentSize.width === viewport.width &&
      attachmentSize.height === viewport.height
    ) {
      return { depth: depthTexture, color: multisampleTexture };
    }

    depthTexture?.destroy();
    multisampleTexture?.destroy();

    depthTexture = device.createTexture({
      size: { width: viewport.width, height: viewport.height },
      format: DEPTH_FORMAT,
      sampleCount: SAMPLE_COUNT,
      usage: GPUTextureUsage.RENDER_ATTACHMENT,
    });
    multisampleTexture = device.createTexture({
      size: { width: viewport.width, height: viewport.height },
      format: renderFormat,
      sampleCount: SAMPLE_COUNT,
      usage: GPUTextureUsage.RENDER_ATTACHMENT,
    });
    attachmentSize = { ...viewport };

    return { depth: depthTexture, color: multisampleTexture };
  };

  type OcclusionAttachments = {
    depthAndId: GPUTexture;
    depthStencil: GPUTexture;
    bindGroup: GPUBindGroup;
  };

  /**
   * Allocated on the first frame that draws occlusion rather than with the other attachments: at
   * 20 bytes a device pixel they are the largest thing the renderer holds, and a view with the
   * occlusion off, or a miniature, should not pay for them.
   */
  const ensureOcclusionAttachments = (viewport: Size): OcclusionAttachments => {
    if (
      occlusionAttachments &&
      occlusionAttachments.depthAndId.width === viewport.width &&
      occlusionAttachments.depthAndId.height === viewport.height
    ) {
      return occlusionAttachments;
    }

    occlusionAttachments?.depthAndId.destroy();
    occlusionAttachments?.depthStencil.destroy();

    const depthAndId = device.createTexture({
      size: { width: viewport.width, height: viewport.height },
      format: OCCLUSION_DEPTH_FORMAT,
      usage:
        GPUTextureUsage.RENDER_ATTACHMENT | GPUTextureUsage.TEXTURE_BINDING,
    });
    occlusionAttachments = {
      depthAndId,
      depthStencil: device.createTexture({
        size: { width: viewport.width, height: viewport.height },
        format: DEPTH_FORMAT,
        usage: GPUTextureUsage.RENDER_ATTACHMENT,
      }),
      bindGroup: device.createBindGroup({
        layout: occlusionPipeline.getBindGroupLayout(0),
        entries: [
          { binding: 0, resource: { buffer: uniformBuffer } },
          { binding: 3, resource: depthAndId.createView() },
        ],
      }),
    };
    return occlusionAttachments;
  };

  return {
    setInstances: instances => {
      nodeBuffer = uploadInstances(nodeBuffer, instances.nodes);
      lineBuffer = uploadInstances(lineBuffer, instances.lines);
      volumeBuffer = uploadInstances(volumeBuffer, instances.volumes);
      arrowBuffer = uploadInstances(arrowBuffer, instances.arrows);
      planarMarkerBuffer = uploadInstances(
        planarMarkerBuffer,
        instances.planarMarkers
      );
      nodeCount = instances.nodeCount;
      lineCount = instances.lineCount;
      guideCount = Math.min(instances.guideCount, instances.lineCount);
      volumeCount = instances.volumeCount;
      arrowCount = instances.arrowCount;
      planarMarkerCount = instances.planarMarkerCount;
    },

    draw: ({
      camera,
      viewport,
      pixelRatio,
      lighting = DEFAULT_LIGHTING,
      ambientOcclusion,
      surface,
      edgeBlend,
      flow,
    }) => {
      if (viewport.width === 0 || viewport.height === 0) return;
      if (
        nodeCount === 0 &&
        lineCount === 0 &&
        volumeCount === 0 &&
        arrowCount === 0 &&
        planarMarkerCount === 0
      ) {
        return;
      }

      device.queue.writeBuffer(
        uniformBuffer,
        0,
        packNetworkUniforms({
          camera,
          viewport,
          pixelRatio,
          lighting,
          ambientOcclusion,
          surface,
          flow,
        })
      );

      const { depth, color } = ensureAttachments(viewport);
      const encoder = device.createCommandEncoder();
      const occlusion =
        ambientOcclusion.strength > 0 && nodeBuffer && nodeCount > 0
          ? ensureOcclusionAttachments(viewport)
          : null;

      if (occlusion && nodeBuffer) {
        const depthPass = encoder.beginRenderPass({
          colorAttachments: [
            {
              view: occlusion.depthAndId.createView(),
              clearValue: { r: 0, g: 0, b: 0, a: 0 },
              loadOp: 'clear',
              storeOp: 'store',
            },
          ],
          depthStencilAttachment: {
            view: occlusion.depthStencil.createView(),
            depthClearValue: 1,
            depthLoadOp: 'clear',
            depthStoreOp: 'discard',
          },
        });
        depthPass.setPipeline(nodeDepthPipeline);
        depthPass.setBindGroup(0, nodeDepthBindGroup);
        depthPass.setVertexBuffer(0, nodeBuffer);
        depthPass.draw(6, nodeCount);
        depthPass.end();
      }
      const pass = encoder.beginRenderPass({
        colorAttachments: [
          {
            view: color.createView(),
            /* The multisampled texture is what is drawn into; the canvas texture receives the
               resolve. Handing the canvas straight to `view` with a sample count above 1 is a
               validation error rather than a silent downgrade. */
            resolveTarget: context
              .getCurrentTexture()
              .createView({ format: renderFormat }),
            clearValue: {
              r: surface[0],
              g: surface[1],
              b: surface[2],
              a: 1,
            },
            loadOp: 'clear',
            storeOp: 'store',
          },
        ],
        depthStencilAttachment: {
          view: depth.createView(),
          /* Cleared to the far plane, since nearer fragments carry smaller z. */
          depthClearValue: 1,
          depthLoadOp: 'clear',
          depthStoreOp: 'store',
        },
      });

      /* Nodes before lines. The line pass does not write depth, so it needs the node depths
         already in the buffer to be occluded by them. */
      if (nodeBuffer && nodeCount > 0) {
        pass.setPipeline(nodePipeline);
        pass.setBindGroup(0, nodeBindGroup);
        pass.setVertexBuffer(0, nodeBuffer);
        pass.draw(6, nodeCount);
      }

      /* After the nodes it darkens and before everything else. The arrowheads are not in the
         depth prepass, so drawn under the multiply they would read as background and take the
         halo; drawn over it they stay crisp. The regions and lines are seen through rather than
         standing in front of anything. */
      if (occlusion) {
        pass.setPipeline(occlusionPipeline);
        pass.setBindGroup(0, occlusion.bindGroup);
        pass.draw(3);
      }

      /* With the nodes rather than with the lines they belong to. An arrowhead stands on a mark's
         surface and is shaded like one, so it is part of the opaque pass that writes depth - which
         is what lets it occlude the references crossing behind it, as the mark beside it does. */
      if (arrowBuffer && arrowCount > 0) {
        pass.setPipeline(arrowPipeline);
        pass.setBindGroup(0, arrowBindGroup);
        pass.setVertexBuffer(0, arrowBuffer);
        pass.draw(ARROW_VERTICES, arrowCount);
      }

      /* Regions after the nodes, so a group's cloud composites over the marks it contains rather
         than being hidden by them, and before the lines, so a reference drawn through a group
         stays a line instead of being washed into it. */
      if (volumeBuffer && volumeCount > 0) {
        const isAdditive = edgeBlend === 'additive';
        pass.setPipeline(isAdditive ? additiveVolumePipeline : volumePipeline);
        pass.setBindGroup(
          0,
          isAdditive ? additiveVolumeBindGroup : volumeBindGroup
        );
        pass.setVertexBuffer(0, volumeBuffer);
        pass.draw(6, volumeCount);
      }

      /* Guides first and never additively, then the routes under whichever blend the theme asked
         for. Order within the buffer is blend order, since neither pass writes depth. */
      if (lineBuffer && guideCount > 0) {
        pass.setPipeline(linePipeline);
        pass.setBindGroup(0, lineBindGroup);
        pass.setVertexBuffer(0, lineBuffer);
        pass.draw(6, guideCount);
      }

      const routeCount = lineCount - guideCount;
      if (lineBuffer && routeCount > 0) {
        const isAdditive = edgeBlend === 'additive';
        pass.setPipeline(isAdditive ? additiveLinePipeline : linePipeline);
        pass.setBindGroup(
          0,
          isAdditive ? additiveLineBindGroup : lineBindGroup
        );
        pass.setVertexBuffer(0, lineBuffer);
        pass.draw(6, routeCount, 0, guideCount);
      }

      /* Last, over the references they belong to. A glyph is notation the reader looks *at*,
         where a region is something they look through, so it is the one thing in this pass that
         is allowed to sit on top of a line. */
      if (planarMarkerBuffer && planarMarkerCount > 0) {
        pass.setPipeline(planarMarkerPipeline);
        pass.setBindGroup(0, planarMarkerBindGroup);
        pass.setVertexBuffer(0, planarMarkerBuffer);
        pass.draw(6, planarMarkerCount);
      }

      pass.end();
      device.queue.submit([encoder.finish()]);
    },

    destroy: () => {
      nodeBuffer?.destroy();
      arrowBuffer?.destroy();
      planarMarkerBuffer?.destroy();
      markerTexture.destroy();
      lineBuffer?.destroy();
      volumeBuffer?.destroy();
      depthTexture?.destroy();
      multisampleTexture?.destroy();
      occlusionAttachments?.depthAndId.destroy();
      occlusionAttachments?.depthStencil.destroy();
      uniformBuffer.destroy();
    },
  };
};

export type NetworkShaderDiagnostic = {
  type: 'error' | 'warning' | 'info';
  lineNumber: number;
  message: string;
};

/**
 * Compiles the shader and returns whatever the driver says about it.
 *
 * Reported rather than logged, for the reason `getShaderDiagnostics` gives: WGSL compilation
 * diagnostics are asynchronous and are not thrown, so a shader with an error produces a module
 * that draws nothing and looks exactly like an empty graph.
 */
export const getNetworkShaderDiagnostics = async (
  device: GPUDevice
): Promise<NetworkShaderDiagnostic[]> => {
  const module = device.createShaderModule({ code: NETWORK_SHADER });
  const info = await module.getCompilationInfo();

  return info.messages.map(({ type, lineNum, message }) => ({
    type,
    lineNumber: lineNum,
    message,
  }));
};
